import { getCurrentDictionary, onLanguageChange, setLanguage, t } from '@/i18n/i18n';

// Simple DOM-level translator that replaces text node contents using a dictionary
// Notes:
// - Skips nodes inside [data-i18n-ignore]
// - Skips SCRIPT/STYLE/NOSCRIPT/INPUT/TEXTAREA/CANVAS/SVG/VIDEO/AUDIO
// - Observes DOM mutations to translate new content
// - Exposes window.deiterateI18n API: setLanguage, translatePage, t, getLocale

type Controller = { translatePage: () => void; disconnect: () => void };

const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEXTAREA', 'INPUT', 'CANVAS', 'SVG', 'VIDEO', 'AUDIO']);

function translateTextNode(node: Text, dict: Record<string, string>): void {
  const original = node.nodeValue;
  if (!original || !original.trim()) return;

  let translated = original;
  // Replace longer phrases first to reduce partial overlaps
  const keys = Object.keys(dict).filter(Boolean).sort((a, b) => b.length - a.length);
  for (const key of keys) {
    const val = dict[key];
    if (!val || key === val) continue;
    // naive global replace; avoid catastrophic cost by limiting long nodes
    if (translated.length > 20000) break;
    translated = translated.split(key).join(val);
  }

  if (translated !== original) {
    node.nodeValue = translated;
  }
}

function shouldSkip(node: Node): boolean {
  const parent = (node as any).parentElement as HTMLElement | null;
  if (!parent) return false;
  if (parent.closest('[data-i18n-ignore]')) return true;
  if (SKIP_TAGS.has(parent.tagName)) return true;
  return false;
}

function walkAndTranslate(root: Node, dict: Record<string, string>) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const value = (node as Text).nodeValue || '';
      if (!value || !value.trim()) return NodeFilter.FILTER_REJECT;
      if (shouldSkip(node)) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  let current: Node | null;
  while ((current = walker.nextNode())) {
    translateTextNode(current as Text, dict);
  }
}

export function startDomTranslator(): Controller {
  const dict = () => getCurrentDictionary();

  function translatePage() {
    if (typeof document === 'undefined' || !document.body) return;
    try {
      walkAndTranslate(document.body, dict());
    } catch (e) {
      console.warn('[i18n] translatePage error', e);
    }
  }

  const observer = new MutationObserver((mutations) => {
    const d = dict();
    for (const m of mutations) {
      if (m.type === 'characterData') {
        const tn = m.target as Text;
        if (tn && !shouldSkip(tn)) translateTextNode(tn, d);
      }
      if (m.type === 'childList') {
        (m.addedNodes || []).forEach((n) => {
          if (n.nodeType === Node.TEXT_NODE) {
            translateTextNode(n as Text, d);
          } else if ((n as Element).nodeType === Node.ELEMENT_NODE) {
            if (!(n as Element).closest('[data-i18n-ignore]')) {
              walkAndTranslate(n, d);
            }
          }
        });
      }
    }
  });

  if (document?.body) {
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  // Re-translate on language change
  onLanguageChange(() => translatePage());

  // Initial pass
  if (document?.readyState === 'complete') {
    translatePage();
  } else {
    window.addEventListener('load', () => translatePage());
  }

  // Expose helper API on window for debugging and imperative use
  if (typeof window !== 'undefined') {
    (window as any).deiterateI18n = {
      setLanguage,
      t,
      translatePage,
      getLocale: () => (localStorage.getItem('i18n_language') || 'en'),
    };
  }

  return {
    translatePage,
    disconnect: () => observer.disconnect(),
  };
}
